import React, { useState, useRef, useEffect } from 'react';
import './Signup.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ChevronLeft, ChevronRight, Check } from 'react-feather';
import { api, ENDPOINTS } from '../../services/apiConfig';

const countryCodes = [
  { code: '+233', country: 'Ghana' },
  { code: '+234', country: 'Togo' },
  { code: '+1', country: 'US/Canada' },
  { code: '+44', country: 'UK' },
  // Add more country codes here...
];

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    othername: '',
    surname: '',
    email: '',
    phoneNumber: '',
    countryCode: '+233', // Default country code
    password: '',
    confirmPassword: '',
  });


  // Refs for inputs
  const othernameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Focus management
  useEffect(() => {
    if (step === 1 && othernameRef.current) othernameRef.current.focus();
    if (step === 2 && emailRef.current) emailRef.current.focus();
    if (step === 3 && passwordRef.current) passwordRef.current.focus();
  }, [step]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const nextStep = () => {
    if (step === 1 && validateStepOne()) setStep(2);
    if (step === 2 && validateStepTwo()) setStep(3);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const validateStepOne = () => {
    if (!formData.othername || !formData.surname) {
      toast.error('Please ensure you fill out your other name and surname.', { 
        position: "top-center"
      });
      return false;
    }
    return true;
  };

  const validateStepTwo = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{9,15}$/;
    
    if (!formData.email || !emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.', { 
        position: "top-center"
      });
      return false;
    }
    
    if (!formData.phoneNumber || !phoneRegex.test(formData.phoneNumber)) {
      toast.error('Please enter a valid phone number (9-15 digits).', { 
        position: "top-center"
      });
      return false;
    }
    
    return true;
  };

  const validateStepThree = () => {
    // Password strength validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    
    if (!formData.password) {
      toast.error('Please enter a password.', { position: "top-center" });
      return false;
    }
    
    if (!passwordRegex.test(formData.password)) {
      toast.error('Password must be at least 8 characters and include uppercase, lowercase, number, and special character.', { position: "top-center" });
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.', { position: "top-center" });
      return false;
    }
    
    registerUser();
    return true;
  };

  const registerUser = async () => {
    setLoading(true);
    const requestBody = {
      othername: formData.othername,
      surname: formData.surname,
      email: formData.email,
      phoneNumber: `${formData.countryCode}${formData.phoneNumber}`,
      password: formData.password,
    };
  
    try {
      // Call the API - note that this already returns response.data from the apiConfig
      const data = await api.vendor.post(ENDPOINTS.vendor.register, requestBody);
      
      // Check if the response contains the success status
      if (data.status === 200 || data.status === 201) {
        toast.success(data.message || "Registration successful!", {
          position: "top-center",
          autoClose: 2000,
          onClose: () => navigate("/vendor-verification"),
        });
        if (data.jwtToken) {
          localStorage.setItem('token', data.jwtToken);
        }
      } else {
        // This is a fallback for unexpected success responses
        toast.warn(data.message || "Unexpected response from server.", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.log("Registration error:", error);
      
      // Handle error from the API
      if (error.response) {
        // Server responded with an error status
        const errorData = error.response.data;
        toast.error(errorData.message || "An error occurred during registration.", {
          position: "top-center",
          autoClose: 3000,
        });
      } else if (error.request) {
        // The request was made but no response was received
        toast.error("Failed to connect to the server. Please check your internet connection and try again.", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        // Something happened in setting up the request
        toast.error("An error occurred during registration. Please try again.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const redirectToLogin = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate("/login");
    }, 800);
  };

  const renderProgressBar = () => {
    return (
      <div className="progress-container">
        <div className="step-indicators">
          <div className={`indicator ${step >= 1 ? 'active' : ''}`}>
            {step > 1 ? <Check size={16} /> : 1}
          </div>
          <div className={`path ${step >= 2 ? 'completed' : ''}`}></div>
          <div className={`indicator ${step >= 2 ? 'active' : ''}`}>
            {step > 2 ? <Check size={16} /> : 2}
          </div>
          <div className={`path ${step >= 3 ? 'completed' : ''}`}></div>
          <div className={`indicator ${step >= 3 ? 'active' : ''}`}>3</div>
        </div>
        <div className="step-labels">
          <span className={step === 1 ? 'current-step' : ''}>Personal Info</span>
          <span className={step === 2 ? 'current-step' : ''}>Contact</span>
          <span className={step === 3 ? 'current-step' : ''}>Security</span>
        </div>
      </div>
    );
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="signup-container">
      <ToastContainer />
      <div className="left-slider">
        <div className="toplogo">
          <img src="assets/genie.png" alt="Genie logo" className="logo-image" />
        </div>
        <div className="slider-content">
          {/* <h1>All-in-one solution for Vendor onboarding</h1> */}
          <p className="slider-tagline">Join thousands of vendors who trust our platform</p>
        </div>
        <div className="slider-card">
          <h3>Vendor Features</h3>
          <div className="features-list">
            <div className="feature">
              <div className="feature-icon">✔</div>
              <div className="feature-text">Quick setup for vendors</div>
            </div>
            <div className="feature">
              <div className="feature-icon">✔</div>
              <div className="feature-text">Integrated payment options</div>
            </div>
            <div className="feature">
              <div className="feature-icon">✔</div>
              <div className="feature-text">24/7 support availability</div>
            </div>
            <div className="feature">
              <div className="feature-icon">✔</div>
              <div className="feature-text">Real-time sales analytics</div>
            </div>
          </div>
        </div>
      </div>

      <div className="right-form">
        {renderProgressBar()}
        
        <div className="form-card">
          <form className="signup-form-step" onSubmit={(e) => e.preventDefault()}>
            {step === 1 && (
              <>
                <h2>Let's get started</h2>
                <p className="form-subtitle">Create your vendor account</p>
                
                <div className="signup-form-group">
                  <label htmlFor="othername">Other Name <span className="required">*</span></label>
                  <input
                    id="othername"
                    type="text"
                    name="othername"
                    value={formData.othername}
                    placeholder="Enter your first name and other names"
                    onChange={handleInputChange}
                    ref={othernameRef}
                    className="input-field"
                  />
                </div>
                
                <div className="signup-form-group">
                  <label htmlFor="surname">Surname <span className="required">*</span></label>
                  <input
                    id="surname"
                    type="text"
                    name="surname"
                    value={formData.surname}
                    placeholder="Enter your surname"
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
                
                <div className="signubtns">
                  <button 
                    className="signup-btn next-btn" 
                    type="button" 
                    onClick={nextStep}
                  >
                    Next <ChevronRight size={18} />
                  </button>
                </div>
              </>
            )}
            
            {step === 2 && (
              <>
                <h2>Contact Details</h2>
                <p className="form-subtitle">How can we reach you?</p>
                
                <div className="signup-form-group">
                  <label htmlFor="email">Email <span className="required">*</span></label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    placeholder="Enter your email address"
                    onChange={handleInputChange}
                    ref={emailRef}
                    className="input-field"
                  />
                </div>
                
                <div className="signup-form-group">
                  <label htmlFor="phoneNumber">Phone Number <span className="required">*</span></label>
                  <div className="phone-number-container">
                    <select
                      id="countryCode"
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleInputChange}
                      className="country-code-selector"
                    >
                      {countryCodes.map((code) => (
                        <option key={code.code} value={code.code}>
                          {code.country} ({code.code})
                        </option>
                      ))}
                    </select>
                    <input
                      id="phoneNumber"
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      placeholder="Enter your phone number"
                      onChange={handleInputChange}
                      className="signup-phone-number-input"
                    />
                  </div>
                </div>
                
                <div className="signubtns">
                  <button 
                    className="signup-btn back-btn" 
                    type="button" 
                    onClick={prevStep}
                  >
                    <ChevronLeft size={18} /> Back
                  </button>
                  <button 
                    className="signup-btn next-btn" 
                    type="button" 
                    onClick={nextStep}
                  >
                    Next <ChevronRight size={18} />
                  </button>
                </div>
              </>
            )}
            
            {step === 3 && (
              <>
                <h2>Set Password</h2>
                <p className="form-subtitle">Secure your account</p>
                
                <div className="signup-form-group">
                  <label htmlFor="password">Password <span className="required">*</span></label>
                  <div className="password-input-container">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      placeholder="Create a strong password"
                      onChange={handleInputChange}
                      ref={passwordRef}
                      className="input-field"
                    />
                    <button 
                      type="button" 
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <div className="password-requirements">
                    <p>Password must contain:</p>
                    <ul>
                      <li className={formData.password.length >= 8 ? 'valid' : ''}>
                        At least 8 characters
                      </li>
                      <li className={/[A-Z]/.test(formData.password) ? 'valid' : ''}>
                        At least one uppercase letter
                      </li>
                      <li className={/[a-z]/.test(formData.password) ? 'valid' : ''}>
                        At least one lowercase letter
                      </li>
                      <li className={/\d/.test(formData.password) ? 'valid' : ''}>
                        At least one number
                      </li>
                      <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'valid' : ''}>
                        At least one special character
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="signup-form-group">
                  <label htmlFor="confirmPassword">Repeat Password <span className="required">*</span></label>
                  <div className="password-input-container">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      placeholder="Confirm your password"
                      onChange={handleInputChange}
                      className="input-field"
                    />
                    <button 
                      type="button" 
                      className="password-toggle"
                      onClick={toggleConfirmPasswordVisibility}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {formData.password && formData.confirmPassword && (
                    <div className={`password-match ${formData.password === formData.confirmPassword ? 'valid' : 'invalid'}`}>
                      {formData.password === formData.confirmPassword ? 
                        "Passwords match ✓" : 
                        "Passwords do not match ✗"}
                    </div>
                  )}
                </div>
                
                <div className="signubtns">
                  <button 
                    className="signup-btn back-btn" 
                    type="button" 
                    onClick={prevStep}
                    disabled={loading}
                  >
                    <ChevronLeft size={18} /> Back
                  </button>
                  <button 
                    className="signup-btn submit-btn" 
                    type="button" 
                    onClick={validateStepThree}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Create Account"}
                  </button>
                </div>
              </>
            )}
            
            <div className="login-redirect">
              <p className="login-text">
                Already have an account? <button type="button" onClick={redirectToLogin} className="login-link">Login</button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;